import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, SITE } from "@/lib/site-data";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#hero"
          data-testid="logo-link"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3 group"
        >
          <img
            src="/assets/luxe-logo.png"
            alt="DISEÑO Y CONSTRUCCIÓN LUXE"
            className="h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            style={{ filter: "invert(1)" }}
          />
          <div className="hidden sm:flex flex-col leading-none border-l border-white/15 pl-3">
            <span className="font-heading font-bold text-white text-sm tracking-widest uppercase">
              Construcción Luxe
            </span>
            <span className="font-body text-[10px] text-[#767171] tracking-[0.25em] uppercase mt-1">
              Diseño · Ingeniería
            </span>
          </div>
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              onClick={(e) => {
                e.preventDefault();
                handleNav(link.href);
              }}
              className="font-body text-sm uppercase tracking-widest text-white/80 hover:text-white relative group transition-colors"
            >
              {link.label}
              <span className="absolute -bottom-2 left-0 w-0 h-[2px] bg-[#385723] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        {/* Phone CTA */}
        <a
          href={`tel:${SITE.phoneRaw}`}
          data-testid="header-phone-cta"
          className="hidden lg:flex items-center gap-3 px-5 py-3 border border-[#385723] hover:bg-[#385723] transition-colors duration-300 group"
        >
          <Phone className="w-4 h-4 text-[#385723] group-hover:text-white transition-colors" />
          <span className="font-heading font-semibold tracking-wider text-sm text-white">
            {SITE.phone}
          </span>
        </a>

        {/* Mobile menu trigger */}
        <button
          data-testid="mobile-menu-trigger"
          aria-label="Menú"
          onClick={() => setOpen(!open)}
          className="lg:hidden w-11 h-11 border border-white/20 flex items-center justify-center text-white hover:border-[#385723] transition-colors"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNav(link.href);
                }}
                className="font-heading uppercase tracking-widest text-white/90 py-3 border-b border-white/10 hover:text-[#385723]"
              >
                {link.label}
              </a>
            ))}
            <a
              href={`tel:${SITE.phoneRaw}`}
              className="flex items-center gap-3 mt-2 px-5 py-4 bg-[#385723] hover:bg-[#4a722e] transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="font-heading font-semibold tracking-wider text-sm">
                {SITE.phone}
              </span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
